import React, { useState } from 'react';
import { Download, RotateCw } from 'lucide-react';
import './Simulation.css';
import { apiService } from '../services/api';
import { Scenario } from '../types/model';
import { downloadBlob } from '../utils/dataProcessing';
import { showToast } from '../utils/toast';

interface ResultsDownloadProps {
  onRebuild?: () => Promise<void>;
  framework: string;
  dataset: string;
  mode: 'baseline' | 'scenarios';
  scenarios?: Scenario[];
}

const ResultsDownload: React.FC<ResultsDownloadProps> = ({
  framework,
  dataset,
  mode,
  scenarios = [], onRebuild,
}) => {
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string>('');
  const [rebuildMsg, setRebuildMsg] = useState<string>('');

  const handleRebuild = async () => {
    setLoading((l) => ({ ...l, rebuild: true }));
    setError('');
    setRebuildMsg('');
    try {
      const res = await apiService.rebuildBaseline(framework, dataset);
      if (onRebuild) await onRebuild();
      setRebuildMsg(`Baseline re-solved (${res.size_kb ?? '?'} KB). ${onRebuild ? 'View updated.' : 'Reload the page to see updated results.'}`);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      setError(`Rebuild failed: ${msg}`);
    } finally {
      setLoading((l) => ({ ...l, rebuild: false }));
    }
  };

  const handleDownload = async (type: 'baseline' | 'scenarios') => {
    setLoading({ ...loading, [type]: true });
    setError('');

    try {
      let blob: Blob;
      let filename: string;

      switch (type) {
        case 'baseline':
          blob = await apiService.downloadBaselineExcel(framework, dataset);
          filename = `baseline_${framework}_${dataset}_${new Date().toISOString().slice(0, 10)}.xlsx`;
          break;

        case 'scenarios': {
          // Excel export understands composable shocks — each scenario is
          // re-solved from its own tariff_config and/or kappa_config.
          const exportable = scenarios.filter((s) => s.status === 'solved');
          if (exportable.length === 0) {
            setError('No solved scenarios to export');
            return;
          }
          blob = await apiService.downloadScenariosExcel(framework, dataset, exportable);
          filename = `Scenarios_${framework}_${dataset}_${new Date().toISOString().slice(0, 10)}.xlsx`;
          break;
        }

        default:
          return;
      }

      downloadBlob(blob, filename);
    } catch (error: any) {
      console.error('Download failed:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setError(`Download failed: ${errorMsg}`);
      showToast(`Failed to download file: ${errorMsg}`, 'error');
    } finally {
      setLoading({ ...loading, [type]: false });
    }
  };

  if (mode === 'baseline') {
    return (
      <div className="result-actions">
        <button
          onClick={() => handleDownload('baseline')}
          disabled={loading.baseline}
          className="simulation-button simulation-button-primary"
          style={{
            ...styles.downloadButton,
            opacity: loading.baseline ? 0.7 : 1,
          }}
        >
          {loading.baseline ? (
            <>
              <span style={styles.spinner}>⏳</span>
              <span>Preparing Excel...</span>
            </>
          ) : (
            <><Download size={16} aria-hidden="true" /> Download Excel</>
          )}
        </button>
        <button
          onClick={handleRebuild}
          disabled={loading.rebuild}
          className="simulation-button simulation-button-secondary"
          style={{ ...styles.downloadButton, opacity: loading.rebuild ? 0.7 : 1 }}
          title="Re-solve and re-cache this framework's baseline (use after the dataset changes)"
        >
          {loading.rebuild ? (
            <>
              <span style={styles.spinner}>⏳</span>
              <span>Rebuilding baseline…</span>
            </>
          ) : (
            <><RotateCw size={16} aria-hidden="true" /> Rebuild baseline</>
          )}
        </button>
        {rebuildMsg && <div role="status" className="result-action-message" style={styles.successText}>{rebuildMsg}</div>}
        {error && <div role="alert" className="result-action-message" style={styles.errorText}>{error}</div>}
      </div>
    );
  }

  // mode === 'scenarios'
  // Excel export supports composable shocks (tariff and/or kappa per scenario).
  const solvedScenarios = scenarios.filter((s) => s.status === 'solved');
  return (
    <div className="result-actions">
      <button
        onClick={() => handleDownload('scenarios')}
        disabled={loading.scenarios || solvedScenarios.length === 0}
        className="simulation-button simulation-button-primary"
      >
        {loading.scenarios
          ? '⏳ Preparing...'
          : `📥 Download ${solvedScenarios.length} Scenario(s)`}
      </button>

      {error && <div role="alert" className="result-action-message" style={styles.errorText}>{error}</div>}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  inlineContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
  },
  spinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: '#fee2e2',
    borderRadius: '6px',
    border: '1px solid #fecaca',
  },
  successText: {
    color: '#166534',
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: '#dcfce7',
    borderRadius: '6px',
    border: '1px solid #bbf7d0',
  },
};

export default ResultsDownload;
